import { addToast } from 'src/toasts'

// F-34. The history tab renders the most recent 100 rows; this fetches the next page.
//
// 🔑 Delegated to #offcanvasRight, which is the persistent container the tab is rendered into — the button
// itself is replaced on every page, so a direct binding would be lost after the first click.
// 📌 The cursor lives on the button as `data-before-id` (the id of the oldest row shown) rather than a page
// number, so a page cannot be skipped or repeated if an audit is written while the tab is open.
// 🚨 **Registered idempotently, because it WAS called twice on some pages.** My first version claimed "only
// one of the customers and clients packs runs on a given page" — wrong: `application.js` calls
// `clientFunctions()` for **every ADMIN** on every desktop page, while `customers/index.js`,
// `quality_controls/index.js`, `events/call_list.js` and the two dashboard packs call `customerFunctions()`.
// An ADMIN on the customers index therefore ran this twice, registered two delegated handlers, and one click
// fired two `$.getScript` calls with the same cursor — `more.js.erb` appending the same 100 rows twice, and
// every later page too, since the replaced button carries the same `before_id`.
// ⚠️ **A SECOND claim in this comment was also wrong, corrected by T-91: it said those two packs "both
// import and invoke `functions_contact_person`", so every handler in the shared trees was double-registered.
// They do not.** `clients/functions.js` imported that module and **never called it** — an unused import,
// which is exactly what made the claim plausible; it has been removed. Measured by listing both
// initialisers' calls: `initHistoryLoadMore()` was the **only** overlap, which is why this was the only
// handler that actually misbehaved.
// ⚠️ **This `off()` IS the defence, not belt-and-braces — an earlier version of this comment claimed the
// opposite and was wrong (corrected 2026-10-08).** It said a `claimOnce` registry "now makes both
// initialisers idempotent, so the double call cannot happen at all". It did not: that registry had exactly
// one call site, and it was not this module — while `initHistoryLoadMore()` is invoked from **both**
// `packs/clients/functions.js` and `packs/customers/functions.js`. This module genuinely is called twice on
// an ADMIN desktop page, and the namespaced `off()` is what stops the second binding.
// ✅ **The registry is gone as of 2026-10-12 (`TODO-ARCHIVE.md` F-113) and this is now the app's single
// mechanism**, which is why the two files can no longer disagree about it. `CLAUDE.md` → *Pack
// Initialisation* has the rationale.
// 📌 This module binds **once**, so `off('click.historyLoadMore').on('click.historyLoadMore', …)` is correct
// here. ⚠️ **A module binding more than once must hoist a single `off('.ns')` above its bindings instead** —
// repeating the paired form would make each `off` wipe the previous bindings and leave only the last
// attached. `packs/customers/functions_contact_person.js` is the three-binding example.
// 📌 `off('click.historyLoadMore')` before `on`, not a module-level `registered` flag: Turbolinks replaces
// the body on every navigation, so `#offcanvasRight` is a new element each time and the handler genuinely
// must be re-registered. A flag that never cleared would block that and the button would stop working after
// the first page. 📌 The `off()` is a no-op on a fresh node, so it costs nothing on the navigation where
// there is nothing to clear — which is what makes this form need no reset hook at all.
export default () => {
  $('#offcanvasRight').off('click.historyLoadMore').on('click.historyLoadMore', '#history-load-more', function (event) {
    event.preventDefault()

    const button = $(this)
    const url = button.data('url')
    const beforeId = button.data('before-id')
    if (!url || !beforeId) return

    // No fullscreen loader: this appends to a list the user is already reading, so blanking the screen
    // would be worse than the wait. The button's own disabled state is the feedback.
    button.prop('disabled', true)

    $.getScript(`${url}?before_id=${encodeURIComponent(beforeId)}`).fail(() => {
      button.prop('disabled', false)
      addToast(I18n.t('errors.unexpected_error'), 'error')
    })
  })
}
